import { scrubPostHogErrorEvent } from '@shared/ui/observability/posthog-error-scrub';
import { resetStorageOnVersionChange } from '@shared/ui/storage/version-reset';
import posthog from 'posthog-js';
import { startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HydratedRouter } from 'react-router/dom';

const posthogToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
if (posthogToken) {
	posthog.init(posthogToken, {
		api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com',
		autocapture: false,
		capture_pageview: false,
		capture_pageleave: false,
		disable_persistence: true,
		disable_session_recording: true,
		person_profiles: 'never',
		capture_exceptions: true,
		before_send: scrubPostHogErrorEvent,
		loaded: (client) =>
			client.register({
				service: 'help',
				environment:
					import.meta.env.VITE_POSTHOG_ENVIRONMENT ?? (import.meta.env.PROD ? 'prod-fe' : 'dev-fe'),
			}),
	});
}

// First thing on boot, before hydration restores the persisted react-query cache:
// if this is a new build, drop all local + session storage so stale-shaped cached
// data can't crash the app (TAS-90). Keeps the auth cookie and the theme mirror.
// Must run before <QueryProvider> mounts. See version-reset.ts.
resetStorageOnVersionChange(__BUILD_ID__);

// Dev-only accessibility check: axe-core scans the rendered page and
// logs any WCAG violations to the console. Runs on axe-core directly (works on
// any React version); dynamically imported so it never ships to prod.
if (import.meta.env.DEV) {
	void import('axe-core').then(({ default: axe }) => {
		setTimeout(() => {
			void axe.run(document.body).then((results) => {
				for (const violation of results.violations) {
					console.warn(`[axe] ${violation.id}: ${violation.help}`, violation.helpUrl);
				}
			});
		}, 1000);
	});
}

// This app ships no service worker. Defensively unregister any a previous build (or
// a shared-origin experiment) left behind, and clear its caches, so a stale worker
// can't intercept navigations with broken/expired responses. Reload once only if we
// actually removed one, to drop back to the live network version.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
	void navigator.serviceWorker
		.getRegistrations()
		.then((registrations) => {
			if (registrations.length === 0) return undefined;
			return Promise.all(registrations.map((registration) => registration.unregister()))
				.then(() => (typeof caches !== 'undefined' ? caches.keys() : []))
				.then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
				.then(() => window.location.reload());
		})
		.catch(() => {
			/* best-effort cleanup */
		});
}

startTransition(() => {
	hydrateRoot(
		document,
		<StrictMode>
			<HydratedRouter />
		</StrictMode>,
	);
});
